/// <reference path='../types/storybook.d.ts' />

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslationInit } from '@amusnet/translations/react'

import {
    BetStepper,
    BetSlider,
    Compliance,
    CustomResult,
    FreeBuyBonusSystem,
    FreeSpinsBonusSystem,
    Game,
    InfoLine,
    InPlayNotification,
    JackpotSection,
    Navigation,
    PlayerInfo,
    FeatureAutostartCounterController,
    Preview,
    RemainingSpinsBannerWrapper,
    ScaledWrapper,
    SpinButtonSection,
    GenericNotification,
    LuckySpins,
    CountdownFreeSpinsBonus,
    AmuseBoost,
    BuyBonus,
    BSLoader,
    PaytableConfirmation,
    DoubleChance,
    NotificationPopup,
} from '@components'
import { UltraPlayModal } from '@components/UltraPlay'

import { useGSAP } from '@gsap/react'
import { useOpacityToggle } from '@hooks'
import {
    useFreeSpinsBonusesStore,
    useBuyBonusStore,
    useComponentStatesStore,
    useConfigurationStore,
    useCustomResultStore,
    useJackpotStore,
    useNavigationStore,
    useSpinButtonSectionStore,
    useFreeBuyBonusStore,
    useDoubleChanceStore,
    useLuckySpinsStore,
    useBetSliderStore,
    useCountdownFSBonusStore,
    useMenuStore,
    useSoundStore,
} from '@stores'
import { fadeGSAPAnimation, patchPaytableUrl } from '@utils/misc'
import {
    BonusSystemState,
    CurrentGameBonusesMessage,
    GameUIMessageType,
    JackpotPhase,
    SetupMessage,
    ActiveBonusFinishedMessage,
    RemainingFreeSpinsUpdateMessage,
    ToggleLeftHandModeMessage,
    JackpotRecoveryMessage,
    GameUIMessageCommand,
    ChangeOpacityComponents,
    GameUIMessageScope,
    Bonus,
    BonusTypes,
    BuyBonusFeature,
    JackpotEQRecoveryMessage,
    DoubleChanceButtonClickedMessage,
    BonusStates,
    FreespinBonusEndModalButtonClickedMessage,
    BuyBonusRejectedMessage,
    BonusesClaimingErrorMessage,
    CountdownFreeSpinsUpdateMessage,
    AutoplayStopCondition,
} from 'game-ui-types'
import { useUltraPlayEngineSync } from '@components/UltraPlay/useUltraPlayEngineSync'
import { BonusImages } from '@constants/bonusSystem'
import { modifyFreeBuyBonuses } from '@utils/bonus'
import gsap from 'gsap'
import { BET_STEPPER_CONFIGS } from '@configs/gameConfigurations'
import { resolvePaytableUrl } from '@stores/configurationStore'
import Promo from '@components/Promo/Promo'
import {
    JackpotMode,
    NotificationType,
    NotificationWrapper,
    showReactUINotification,
    isStandalone,
    getParam,
    cn,
    PostMessageSystemUI,
} from '@amusnet/react-ui'

const App: React.FC = () => {
    // configuration store
    const isHistoryMode = useConfigurationStore((state) => state.isHistoryMode)
    const setConfiguration = useConfigurationStore((state) => state.setConfiguration)
    const hasCustomResult = useConfigurationStore((state) => state.hasCustomResult)
    const isGameLoaded = useConfigurationStore((state) => state.isGameLoaded)
    const setIsGameLoaded = useConfigurationStore((state) => state.setIsGameLoaded)
    const hasLuckySpins = useConfigurationStore((state) => state.hasLuckySpins)
    const currentGameBonuses = useConfigurationStore((state) => state.currentGameBonuses)
    const hasBuyBonus = useConfigurationStore((state) => state.hasBuyBonus)
    const buyBonusConfigFromStore = useConfigurationStore((state) => state.buyBonusConfig)
    const gameType = useConfigurationStore((state) => state.gameType)
    const hasDoubleChance = useConfigurationStore((state) => state.hasDoubleChance)
    const shouldUseExternalMenu = useConfigurationStore((state) => state.shouldUseExternalMenu)
    const setPaytableData = useConfigurationStore((state) => state.setPaytableData)
    const shouldShowPaytableConfirmation = useConfigurationStore((state) => state.shouldShowPaytableConfirmation)
    const isPaytableConfirmationAccepted = useConfigurationStore((state) => state.isPaytableConfirmationAccepted)
    const shouldUseAmuseBoost = useConfigurationStore((state) => state.shouldUseAmuseBoost)
    // TODO: remove when promo removed from platform
    const hasWidgetInUI = useConfigurationStore((state) => state.hasWidgetInUI)
    const viewport = useConfigurationStore((state) => state.viewport)
    const setPlayerUid = useConfigurationStore((state) => state.setPlayerUid)
    const setApiBaseUrl = useConfigurationStore((state) => state.setApiBaseUrl)
    const setPortalUid = useConfigurationStore((state) => state.setPortalUid)
    const setPortalCode = useConfigurationStore((state) => state.setPortalCode)
    const setPromoModes = useConfigurationStore((state) => state.setPromoModes)

    // buy bonus store
    const setBuyBonusConfig = useBuyBonusStore((state) => state.setBuyBonusConfig)
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)
    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)
    const updateCurrentFeatureMultiplier = useBuyBonusStore((state) => state.updateCurrentFeatureMultiplier)
    const setHasGuaranteedWin = useBuyBonusStore((state) => state.setHasGuaranteedWin)
    const setIsForceGuaranteedWinEnabled = useBuyBonusStore((state) => state.setIsForceGuaranteedWinEnabled)
    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)

    // double chance store
    const isDoubleChanceActivated = useDoubleChanceStore((state) => state.isDoubleChanceActivated)
    const setIsDoubleChanceActivated = useDoubleChanceStore((state) => state.setIsDoubleChanceActivated)

    // lucky spins store
    const setLSActiveBonus = useLuckySpinsStore((state) => state.setActiveBonus)
    const setLSRemainingSpins = useLuckySpinsStore((state) => state.setRemainingSpins)
    const setLSIsIntro = useLuckySpinsStore((state) => state.setIsIntro)
    const setLSActiveBet = useLuckySpinsStore((state) => state.setActiveBet)
    const setIsLSModalOpened = useLuckySpinsStore((state) => state.setIsModalOpened)
    const setLSTotalWin = useLuckySpinsStore((state) => state.setTotalWin)
    const setLSShowRemainingSpinsBanner = useLuckySpinsStore((state) => state.setShowRemainingSpinsBanner)
    const setAreLSAboutToStart = useLuckySpinsStore((state) => state.setAreLSAboutToStart)
    const setIsLSRecovery = useLuckySpinsStore((state) => state.setIsRecovery)
    const isLSRecovery = useLuckySpinsStore((state) => state.isRecovery)
    // TODO: remove when promo removed from platform
    const showLuckySpins = useLuckySpinsStore((state) => state.showLuckySpins)
    const setShowLuckySpins = useLuckySpinsStore((state) => state.setShowLuckySpins)

    const setCFSBonuses = useCountdownFSBonusStore((state) => state.setBonuses)
    const setCFSActiveBonus = useCountdownFSBonusStore((state) => state.setActiveBonus)
    const setCFSActiveBet = useCountdownFSBonusStore((state) => state.setActiveBet)
    const setIsCFSModalOpened = useCountdownFSBonusStore((state) => state.setIsModalOpened)
    const setCFSTotalWin = useCountdownFSBonusStore((state) => state.setTotalWin)
    const setCFSShowRemainingSpinsBanner = useCountdownFSBonusStore((state) => state.setShowRemainingSpinsBanner)
    const setCFSBonusSystemState = useCountdownFSBonusStore((state) => state.setBonusSystemState)
    const setCFSClaimingError = useCountdownFSBonusStore((state) => state.setIsThereClaimingError)
    const setIsCFSBonusAlreadyClaimed = useCountdownFSBonusStore((state) => state.setIsBonusAlreadyClaimed)
    const setCFSHasActiveBonusEnded = useCountdownFSBonusStore((state) => state.setHasActiveBonusEnded)
    const setCFSHasActiveBonusesLeft = useCountdownFSBonusStore((state) => state.setHasActiveBonusesLeft)
    const setIsCFSRecovery = useCountdownFSBonusStore((state) => state.setIsRecovery)
    const isCFSRecovery = useCountdownFSBonusStore((state) => state.isRecovery)

    // free spins bonuses store
    const setFSBonuses = useFreeSpinsBonusesStore((state) => state.setBonuses)
    const setFSTotalWin = useFreeSpinsBonusesStore((state) => state.setTotalWin)
    const setFSHasActiveBonusEnded = useFreeSpinsBonusesStore((state) => state.setHasActiveBonusEnded)
    const setFSHasActiveBonusesLeft = useFreeSpinsBonusesStore((state) => state.setHasActiveBonusesLeft)
    const setFSRemainingSpins = useFreeSpinsBonusesStore((state) => state.setRemainingSpins)
    const setFSActiveBet = useFreeSpinsBonusesStore((state) => state.setActiveBet)
    const setFSShowRemainingSpinsBanner = useFreeSpinsBonusesStore((state) => state.setShowRemainingSpinsBanner)
    const setFSBonusSystemState = useFreeSpinsBonusesStore((state) => state.setBonusSystemState)
    const setBonusSpinsClaimingError = useFreeSpinsBonusesStore((state) => state.setIsThereClaimingError)
    const setIsFSBonusAlreadyClaimed = useFreeSpinsBonusesStore((state) => state.setIsBonusAlreadyClaimed)
    const setIsFSRecovery = useFreeSpinsBonusesStore((state) => state.setIsRecovery)
    const isFSRecovery = useFreeSpinsBonusesStore((state) => state.isRecovery)
    const setIsWaitingForFSBonusSystemResponse = useFreeSpinsBonusesStore(
        (state) => state.setIsWaitingForBonusSystemResponse
    )

    // free buy bonus store
    const FBBActiveBonus = useFreeBuyBonusStore((state) => state.activeBonus)
    const FBBBonuses = useFreeBuyBonusStore((state) => state.bonuses)
    const setFBBonuses = useFreeBuyBonusStore((state) => state.setBonuses)
    const setFBBonusSystemState = useFreeBuyBonusStore((state) => state.setBonusSystemState)
    const setHasAwardedFreeBuyBonuses = useFreeBuyBonusStore((state) => state.setHasAwardedFreeBuyBonuses)
    const setFBBStyles = useFreeBuyBonusStore((state) => state.setStyles)
    const setBundleImageName = useFreeBuyBonusStore((state) => state.setBundleImageName)
    const setFBBActiveBonus = useFreeBuyBonusStore((state) => state.setActiveBonus)
    const setFBBTotalWin = useFreeBuyBonusStore((state) => state.setTotalWin)
    const setHasFBBActivatedBonus = useFreeBuyBonusStore((state) => state.setHasActivatedBonus)
    const setFBBClaimingError = useFreeBuyBonusStore((state) => state.setIsThereClaimingError)
    const setIsFBBonusAlreadyClaimed = useFreeBuyBonusStore((state) => state.setIsBonusAlreadyClaimed)
    const setFBBLastBonus = useFreeBuyBonusStore((state) => state.setLastBonus)
    const activeFBBonus = useFreeBuyBonusStore((state) => state.activeBonus)
    const setIsWaitingForFBBonusSystemResponse = useFreeBuyBonusStore(
        (state) => state.setIsWaitingForBonusSystemResponse
    )
    const setFBHasActiveBonusesLeft = useFreeBuyBonusStore((state) => state.setHasActiveBonusesLeft)

    // component states store
    const isBuyBonusComponentActive = useComponentStatesStore((state) => state.isBuyBonusComponentActive)
    const isComplianceComponentActive = useComponentStatesStore((state) => state.isComplianceComponentActive)
    const isJackpotCardsComponentActive = useComponentStatesStore((state) => state.isJackpotCardsComponentActive)
    const isMainButtonsComponentActive = useComponentStatesStore((state) => state.isMainButtonsComponentActive)
    const isPlayerInfoComponentActive = useComponentStatesStore((state) => state.isPlayerInfoComponentActive)
    const isInfoLineComponentActive = useComponentStatesStore((state) => state.isInfoLineComponentActive)
    const setIsExternalUIComponentActive = useComponentStatesStore((state) => state.setIsExternalUIComponentActive)
    const setComponentStates = useComponentStatesStore((state) => state.setComponentStates)

    // custom result store
    const isCustomResultOpen = useCustomResultStore((state) => state.isOpened)

    // jackpot store
    const setIsJackpotSettingActive = useJackpotStore((state) => state.setIsJackpotSettingActive)
    const setJackpotType = useJackpotStore((state) => state.setJackpotType)
    const setJackpotGameState = useJackpotStore((state) => state.setJackpotGameState)
    const setJackpotPhase = useJackpotStore((state) => state.setJackpotPhase)
    const setCurrencyHideCentsOnlyInJackpotBoxes = useJackpotStore(
        (state) => state.setCurrencyHideCentsOnlyInJackpotBoxes
    )
    const setToolTipDateSeparator = useJackpotStore((state) => state.setToolTipDateSeparator)
    const hasJackpot = useJackpotStore((state) => state.isJackpotSettingActive)
    const setIsJackpotEQStarted = useJackpotStore((state) => state.setIsJackpotEQStarted)
    const setFreezeIndex = useJackpotStore((state) => state.setFreezeIndex)
    const setJackpotMode = useJackpotStore((state) => state.setMode)

    // spin button section store
    const updateIsLeftHandMode = useSpinButtonSectionStore((state) => state.updateIsLeftHandMode)

    // bet slider store
    const betGroup = useBetSliderStore((state) => state.betGroup)
    const configureBetGroup = useBetSliderStore((state) => state.configureBetGroup)

    // navigation store
    const isMenuOpen = useNavigationStore((state) => state.isMenuOpen)

    // menu store
    const setWinExceedsValue = useMenuStore((state) => state.setWinExceedsValue)
    const setBalanceDecreasesValue = useMenuStore((state) => state.setBalanceDecreasesValue)
    const setBalanceIncreasesValue = useMenuStore((state) => state.setBalanceIncreasesValue)

    // sound store
    const initSound = useSoundStore((state) => state.init)

    const bonusFeaturesRef = useRef<BuyBonusFeature[]>([])

    const hasUltraPlay = useConfigurationStore((state) => state.hasUltraPlay)

    // All Ultra Play <-> engine message syncing lives in its own hook (own listener).
    // No-ops internally while the feature flag is off.
    useUltraPlayEngineSync()

    const isInStorybook = typeof window !== 'undefined' && !!window.__STORYBOOK_PREVIEW__
    const mainComponents = useRef<HTMLDivElement | null>(null)
    const underNavComponents = useRef<HTMLDivElement | null>(null)
    const overNavComponents = useRef<HTMLDivElement | null>(null)

    const { isComponentInteractive } = useOpacityToggle(ChangeOpacityComponents.EXTERNAL_UI, mainComponents)

    const language = useConfigurationStore((state) => state.language)
    const setLanguage = useConfigurationStore((state) => state.setLanguage)
    const setPaytableUrl = useConfigurationStore((state) => state.setPaytableUrl)
    const setPaytableBasePath = useConfigurationStore((state) => state.setPaytableBasePath)

    const [stageScale, setStageScale] = useState(1)

    useTranslationInit(language, {
        projects: ['common', 'gameui'],
        version: getParam('translationsVersion'),
        baseUrl: getParam('baseUrl'),
    })

    useGSAP(
        () => {
            if (shouldUseExternalMenu) return

            if (!underNavComponents.current || !overNavComponents.current) return

            fadeGSAPAnimation(!isMenuOpen, [underNavComponents.current, overNavComponents.current])
        },
        { dependencies: [isMenuOpen, shouldUseExternalMenu] }
    )

    useEffect(() => {
        if (hasWidgetInUI) {
            if (isGameLoaded && !isHistoryMode && !isCFSRecovery && !isLSRecovery && !isFSRecovery && hasLuckySpins) {
                showReactUINotification({ type: NotificationType.LUCKY_SPINS })
            }

            return
        }

        if (isGameLoaded && hasLuckySpins) {
            setShowLuckySpins(true)
        }
    }, [isGameLoaded, hasLuckySpins])

    const renderMainComponents = useMemo(() => {
        const useBetStepper = betGroup || BET_STEPPER_CONFIGS.includes(gameType)

        return (
            <>
                <div style={{ display: isCustomResultOpen ? 'none' : 'block' }}>
                    <div ref={underNavComponents}>
                        {!isHistoryMode && !shouldUseAmuseBoost && <DoubleChance key="double-chance" />}
                        {!isHistoryMode &&
                            (shouldUseAmuseBoost && hasDoubleChance ? (
                                <AmuseBoost key="amuse-boost" />
                            ) : (
                                <BuyBonus key="buy-bonus" />
                            ))}
                        {!isHistoryMode && isComplianceComponentActive && <Compliance key="compliance" />}
                        {!isHistoryMode && hasJackpot && isJackpotCardsComponentActive && (
                            <JackpotSection key="jackpot" />
                        )}
                        {!isHistoryMode && isMainButtonsComponentActive && !useBetStepper && (
                            <BetSlider key="bet-slider" />
                        )}
                        {!isHistoryMode && isMainButtonsComponentActive && useBetStepper && (
                            <BetStepper key="bet-stepper" />
                        )}
                        {!isHistoryMode && hasWidgetInUI && <Promo key="promo" stageScale={stageScale} />}
                        {!isHistoryMode && (
                            <>
                                <RemainingSpinsBannerWrapper key="remaining-spins-banner-wrapper" />
                                <div className="z-[1000] relative">
                                    {isBuyBonusComponentActive && <FreeBuyBonusSystem key="free-buy-bonus-modal" />}
                                    <FreeSpinsBonusSystem key="free-spins-modal" />
                                    <LuckySpins key="lucky-spins" />
                                    <CountdownFreeSpinsBonus key="countdown-free-spins-bonus" />
                                    <GenericNotification key="claiming-error-notification" />
                                </div>
                            </>
                        )}
                    </div>
                    {!isHistoryMode && isMainButtonsComponentActive && <Navigation key="navigation" />}
                    {!isHistoryMode && isBuyBonusComponentActive && <InPlayNotification key="free-buy-bonus-modal" />}
                    {!isHistoryMode && hasWidgetInUI && (
                        <NotificationWrapper className="z-[1002]" key="notification-wrapper" />
                    )}
                    {!isHistoryMode && isBuyBonusComponentActive && <BSLoader />}
                </div>
                <div ref={overNavComponents}>
                    {!isHistoryMode && isMainButtonsComponentActive && <SpinButtonSection key="spin-button-section" />}
                    <div style={{ display: isCustomResultOpen ? 'none' : 'block' }}>
                        {!isHistoryMode && isPlayerInfoComponentActive && <PlayerInfo key="player-info" />}
                        {isInfoLineComponentActive && <InfoLine key="info-line" />}
                    </div>
                </div>
            </>
        )
    }, [
        isBuyBonusComponentActive,
        isComplianceComponentActive,
        isJackpotCardsComponentActive,
        isMainButtonsComponentActive,
        isPlayerInfoComponentActive,
        isInfoLineComponentActive,
        isHistoryMode,
        hasJackpot,
        isCustomResultOpen,
        hasDoubleChance,
        shouldUseAmuseBoost,
        hasWidgetInUI,
        betGroup,
        gameType,
        stageScale,
    ])

    const getNextPriorityBonusFromBonusesArray = (bonuses: Bonus[], type?: BonusTypes): Bonus | null => {
        let usedBonus: Bonus | null = null
        let inProgressBonus: Bonus | null = null
        let triggeringBetCompletedBonus: Bonus | null = null
        let aggregatedGameStartedBonus: Bonus | null = null
        let inPlayBonus: Bonus | null = null

        for (const b of bonuses) {
            if (type && b.type !== type) continue

            if (!b.isUnused) usedBonus = b

            if (b.gameRoundInProgress) inProgressBonus = b

            if (b.triggeringBetCompleted) triggeringBetCompletedBonus = b

            if (b.aggregatedGameStarted) aggregatedGameStartedBonus = b

            if (b.state === BonusStates.IN_PLAY) inPlayBonus = b
        }

        return usedBonus ?? inProgressBonus ?? triggeringBetCompletedBonus ?? aggregatedGameStartedBonus ?? inPlayBonus
    }

    useEffect(() => {
        window.loadGameScripts()
    }, [])

    useEffect(() => {
        if (window.bottle) {
            initSound()
        }
    }, [window.bottle])

    useLayoutEffect(() => {
        setIsExternalUIComponentActive(isComponentInteractive)
    }, [isComponentInteractive])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const rawData = event.data
            const type = rawData.type

            if (event.data?.data?.command === GameUIMessageCommand.START) {
                // TODO: workaround for the case where the ui loading before the game is loaded
                setTimeout(() => {
                    setIsGameLoaded(true)
                }, 900)
            }

            if (event.data?.data?.command === GameUIMessageCommand.SHOW) {
                const {
                    language: requestedLanguage,
                    translationsLanguages = [],
                    paytableUrl,
                    paytableBasePath,
                    playerUid,
                    apiBaseUrl,
                    portalUid,
                    portalCode,
                    promoModes,
                } = event.data.data

                // Normalize and validate the requested language if it is supported
                const normalizedLanguage = `${requestedLanguage.slice(0, 2).toLowerCase()}${requestedLanguage.slice(2).toUpperCase()}`

                if (!translationsLanguages.length || translationsLanguages.includes(normalizedLanguage)) {
                    setLanguage(normalizedLanguage)
                }

                if (paytableBasePath) {
                    setPaytableBasePath(paytableBasePath)
                }

                const { paytableLanguages, language: currentLang } = useConfigurationStore.getState()

                // On show (before setup), paytableLanguages is empty so this
                // stores the platform URL directly (paytableUrl).
                // TODO: Remove this after all engines are updated to send paytableLanguages
                setPaytableUrl(resolvePaytableUrl(currentLang, paytableLanguages, paytableBasePath, paytableUrl))

                setPlayerUid(playerUid)
                setApiBaseUrl(apiBaseUrl)
                setPortalUid(portalUid)
                setPortalCode(portalCode)
                setPromoModes(promoModes)
            }

            // TODO: remove when promo removed from platform
            if (
                event.data.id == 'EventForGame' &&
                event.data.data.command === 'showLuckySpinsNotification' &&
                !hasWidgetInUI
            ) {
                setShowLuckySpins(true)
            }

            switch (type) {
                case GameUIMessageType.SETUP: {
                    const data = rawData as SetupMessage

                    if (data.componentStates) setComponentStates(data.componentStates)

                    if (data.paytableData) setPaytableData(data.paytableData)

                    if (data.configuration) {
                        setConfiguration(data.configuration)

                        // New engine version sends paytableLanguages in the configuration.
                        // If it's available, resolve the paytable URL from basePath + current language.
                        const {
                            paytableLanguages,
                            paytableBasePath: currentBasePath,
                            language: currentLang,
                            paytableUrl: currentPaytableUrl,
                        } = useConfigurationStore.getState()

                        if (paytableLanguages.length > 0) {
                            setPaytableUrl(
                                resolvePaytableUrl(currentLang, paytableLanguages, currentBasePath, currentPaytableUrl)
                            )
                        }

                        if (data.configuration.hasDoubleChance) {
                            if (data.configuration.initialFeatureName === 'DOUBLE_CHANCE') {
                                setIsDoubleChanceActivated(true)

                                const doubleChanceClickMessage: DoubleChanceButtonClickedMessage = {
                                    type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
                                    scope: GameUIMessageScope.DOUBLE_CHANCE,
                                }

                                window.postMessage(doubleChanceClickMessage)

                                updateCurrentFeatureMultiplier(data.configuration.doubleChanceMultiplier || 1)
                            }
                        }

                        if (data.configuration.buyBonusConfig) {
                            const { buyBonusConfig, initialFeatureName } = data.configuration
                            const { features, hasGuaranteedWin, shouldTheFeatureStayActive } = buyBonusConfig

                            setBuyBonusConfig(buyBonusConfig)

                            bonusFeaturesRef.current = features

                            setHasGuaranteedWin(hasGuaranteedWin)

                            if (shouldTheFeatureStayActive) {
                                const currentFeatureIndex = features.findIndex(
                                    (feature: BuyBonusFeature) =>
                                        feature.name === initialFeatureName ||
                                        feature.guaranteedName === initialFeatureName
                                )
                                const currentFeature =
                                    currentFeatureIndex === -1 ? undefined : features[currentFeatureIndex]

                                if (currentFeature && currentFeature.name !== 'NONE') {
                                    const guaranteedMultiplier =
                                        currentFeature.guaranteedName === initialFeatureName
                                            ? (currentFeature.guaranteedMultiplier ?? currentFeature.multiplier)
                                            : currentFeature.multiplier

                                    setMultiChoiceToggle(true)

                                    setSelectedFeature(currentFeature)

                                    setIsForceGuaranteedWinEnabled(currentFeature.guaranteedName === undefined)

                                    setSelectedFeatureIndex(currentFeatureIndex)

                                    updateCurrentFeatureMultiplier(guaranteedMultiplier)
                                }
                            }

                            // TODO: this config will be taken from `data.configuration` -> this change will be introduced in the next PR  with the remaining free buy bonus functionalities
                            setFBBStyles({
                                footerAndHeaderBackground: [
                                    data.configuration.buyBonusConfig.layout.headerColors,
                                    '90deg',
                                ],
                                bodyBackground: [data.configuration.buyBonusConfig.layout.bodyColors, '180deg'],
                                bundleBackground: [
                                    data.configuration.buyBonusConfig.layout.symbolBackgroundColors?.length
                                        ? data.configuration.buyBonusConfig.layout.symbolBackgroundColors
                                        : data.configuration.buyBonusConfig.layout.bodyColors,
                                    '180deg',
                                ],
                                bundleContainerBackground: [
                                    data.configuration.buyBonusConfig.layout.bundleContainerBackgroundColor || [
                                        '#000000',
                                    ],
                                    '180deg',
                                ],
                                headerLabelColor: data.configuration.buyBonusConfig.layout.headerLabelColor || '',
                                primaryTextColor: data.configuration.buyBonusConfig.layout.labelColor || '#FFE365',
                                secondaryTextColor: data.configuration.buyBonusConfig.layout.moneyColor || '',
                                secondaryTextColorBundle:
                                    data.configuration.buyBonusConfig.layout.moneyColorBundle || '', // we want FBB bundles to always fall back to white secondary
                                playBtnColor: data.configuration.buyBonusConfig.layout.playBtnColor || '#A6CE39',
                                playLaterBtnColor:
                                    data.configuration.buyBonusConfig.layout.playLaterBtnColor || '#D9D9D9',
                                declineBtnColor: '#D9D9D9',
                                imageName: BonusImages.FREE_BUY_BONUS_MAIN,
                            })
                        }

                        if (data.configuration.betOptions) {
                            configureBetGroup(
                                data.configuration.betOptions,
                                data.configuration.betGroupId,
                                data.configuration.betOptionActiveIndex
                            )
                        }

                        // Check if there is a configuration value for autoplay limits when the component is initialized, for example on reconnect
                        const { autoplaySettings } = data.configuration

                        if (autoplaySettings) {
                            if (autoplaySettings[AutoplayStopCondition.SINGLE_WIN] !== -1) {
                                setWinExceedsValue(autoplaySettings[AutoplayStopCondition.SINGLE_WIN])
                            }

                            if (autoplaySettings[AutoplayStopCondition.BALANCE_DECREASE] !== -1) {
                                setBalanceDecreasesValue(autoplaySettings[AutoplayStopCondition.BALANCE_DECREASE])
                            }

                            if (autoplaySettings[AutoplayStopCondition.BALANCE_INCREASE] !== -1) {
                                setBalanceIncreasesValue(autoplaySettings[AutoplayStopCondition.BALANCE_INCREASE])
                            }
                        }

                        // Jackpot Init
                        const {
                            jackpot,
                            jackpotType,
                            currencyHideCentsOnlyInJackpotBoxes,
                            toolTipDateSeparator,
                            hasInPlayFreeSpins,
                        } = data.configuration

                        if (jackpot !== undefined) setIsJackpotSettingActive(jackpot)

                        if (jackpotType) setJackpotType(jackpotType)

                        if (currencyHideCentsOnlyInJackpotBoxes !== undefined)
                            setCurrencyHideCentsOnlyInJackpotBoxes(currencyHideCentsOnlyInJackpotBoxes)

                        if (toolTipDateSeparator) setToolTipDateSeparator(toolTipDateSeparator)

                        if (!data.configuration.currentGameBonuses) {
                            // At the moment of receiving currentGameBonuses we haven't received a buyBonusConfig yet which means that there are no bonusFeaturesRef.current
                            if (activeFBBonus) {
                                const isSingleFBB = FBBBonuses.length === 1
                                const freeBuyBonuses = modifyFreeBuyBonuses(
                                    isSingleFBB ? [activeFBBonus] : FBBBonuses,
                                    bonusFeaturesRef.current
                                )

                                setFBBonuses(freeBuyBonuses)
                            }

                            return
                        }

                        // ------- Bonuses Recovery -------
                        const currentGameBonusesData = data.configuration.currentGameBonuses

                        const currentCountdownFSBonus = getNextPriorityBonusFromBonusesArray(
                            currentGameBonusesData,
                            BonusTypes.COUNTDOWN_FREE_SPIN
                        )

                        // COUNTDOWN FREESPIN BONUS
                        if (currentCountdownFSBonus) {
                            const { totalWin, betValue } = currentCountdownFSBonus

                            setCFSActiveBonus(currentCountdownFSBonus)
                            setCFSTotalWin(`${totalWin}`)
                            setCFSActiveBet(betValue)
                            setCFSShowRemainingSpinsBanner(true)
                            setCFSBonuses(
                                currentGameBonusesData.filter((b) => b.type === BonusTypes.COUNTDOWN_FREE_SPIN)
                            )
                            setIsCFSRecovery(true)

                            return
                        }

                        const currentLSBonus = getNextPriorityBonusFromBonusesArray(
                            currentGameBonusesData,
                            BonusTypes.LUCKY_SPIN
                        )

                        // LUCKY SPINS
                        if (currentLSBonus && (!currentLSBonus.isUnused || currentLSBonus.triggeringBetCompleted)) {
                            const { totalWin, remainingAmount, betValue } = currentLSBonus

                            setLSActiveBonus(currentLSBonus)
                            setLSTotalWin(`${totalWin}`)

                            if (remainingAmount) setLSRemainingSpins(remainingAmount)

                            setLSActiveBet(betValue)
                            setLSIsIntro(true)
                            setLSShowRemainingSpinsBanner(true)
                            setIsLSRecovery(true)

                            return
                        }

                        // FREESPIN BONUS
                        const currentFSBonus = getNextPriorityBonusFromBonusesArray(
                            currentGameBonusesData,
                            BonusTypes.FREE_SPIN
                        )

                        if (currentFSBonus && hasInPlayFreeSpins !== undefined) {
                            setFSShowRemainingSpinsBanner(hasInPlayFreeSpins)
                            setIsFSRecovery(true)

                            return
                        }

                        // FREE BUY BONUS
                        const freeBuyBonuses = modifyFreeBuyBonuses(currentGameBonusesData, bonusFeaturesRef.current)

                        if (!freeBuyBonuses.length) return

                        setFBBonuses(freeBuyBonuses)

                        const currentFBBonus = getNextPriorityBonusFromBonusesArray(
                            freeBuyBonuses,
                            BonusTypes.FREE_BUY_BONUS
                        )

                        if (currentFBBonus) {
                            setHasFBBActivatedBonus(true)
                            setFBBActiveBonus(currentFBBonus)
                        }
                    }

                    break
                }
                case GameUIMessageType.PLAY_NOW_REQUEST: {
                    setIsWaitingForFSBonusSystemResponse(true)
                    setIsWaitingForFBBonusSystemResponse(true)
                    break
                }
                case GameUIMessageType.PLAY_NOW_OUTCOME: {
                    setIsWaitingForFSBonusSystemResponse(false)
                    setIsWaitingForFBBonusSystemResponse(false)
                    break
                }
                case GameUIMessageType.CURRENT_GAME_BONUSES: {
                    const data = rawData as CurrentGameBonusesMessage

                    setFBBActiveBonus(undefined)

                    // Reset Double Chance
                    if (isDoubleChanceActivated) {
                        setIsDoubleChanceActivated(false)

                        const doubleChanceClickMessage: DoubleChanceButtonClickedMessage = {
                            type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
                            scope: GameUIMessageScope.DOUBLE_CHANCE,
                        }

                        window.postMessage(doubleChanceClickMessage)

                        updateCurrentFeatureMultiplier(1)
                    }

                    // Reset Buy Bonus
                    if (hasBuyBonus && buyBonusConfigFromStore.shouldTheFeatureStayActive) {
                        updateCurrentFeatureMultiplier(1)
                        setMultiChoiceToggle(false)

                        const buyBonusRejectedMessage: BuyBonusRejectedMessage = {
                            type: GameUIMessageType.BUY_BONUS_REJECTED,
                        }

                        window.postMessage(buyBonusRejectedMessage)
                    }

                    const currentCountdownFSBonus = getNextPriorityBonusFromBonusesArray(
                        data.bonuses ?? [],
                        BonusTypes.COUNTDOWN_FREE_SPIN
                    )

                    // COUNTDOWN FREESPIN BONUS
                    if (currentCountdownFSBonus && +(currentCountdownFSBonus.expirationTime ?? 0) - Date.now() > 0) {
                        const { totalWin, betValue } = currentCountdownFSBonus

                        setCFSBonusSystemState(
                            currentCountdownFSBonus.state === BonusStates.IN_PLAY
                                ? BonusSystemState.IN_PLAY
                                : BonusSystemState.SINGLE_BONUS_INITIAL
                        )
                        setCFSActiveBonus(currentCountdownFSBonus)
                        setCFSTotalWin(`${totalWin}`)
                        setCFSActiveBet(betValue)
                        setIsCFSModalOpened(true)
                        setCFSShowRemainingSpinsBanner(true)

                        return
                    } else {
                        const countdownBonuses = data.bonuses.filter(
                            (bonus: Bonus) => bonus.type === BonusTypes.COUNTDOWN_FREE_SPIN
                        )

                        setCFSBonuses(countdownBonuses)

                        if (countdownBonuses.length) {
                            setCFSBonusSystemState(BonusSystemState.SINGLE_BONUS_INITIAL)
                        }
                    }

                    const currentLSBonus = getNextPriorityBonusFromBonusesArray(
                        data.bonuses ?? [],
                        BonusTypes.LUCKY_SPIN
                    )

                    // LUCKY SPINS
                    if (currentLSBonus && (!currentLSBonus.isUnused || currentLSBonus.triggeringBetCompleted)) {
                        const { totalWin, remainingAmount, betValue } = currentLSBonus

                        setLSActiveBonus(currentLSBonus)
                        setLSTotalWin(`${totalWin}`)

                        if (remainingAmount) setLSRemainingSpins(remainingAmount)

                        setLSActiveBet(betValue)
                        setLSIsIntro(true)
                        setIsLSModalOpened(true)
                        setLSShowRemainingSpinsBanner(true)

                        return
                    }

                    // FREE SPINS
                    const freeSpins = (data.bonuses as Bonus[]).filter(
                        (bonus: Bonus) => bonus.type === BonusTypes.FREE_SPIN
                    )

                    // If we choose to play a bonus later and then we recover, we need to re-establish them as awarded so we can play them.
                    freeSpins.map((b) => {
                        if (b.state === BonusStates.CLAIMED && b.isUnused) {
                            b.state = BonusStates.AWARDED
                        }
                    })

                    // Reset Buy Bonus
                    if (hasBuyBonus && buyBonusConfigFromStore.shouldTheFeatureStayActive) {
                        updateCurrentFeatureMultiplier(1)
                        setMultiChoiceToggle(false)

                        const buyBonusRejectedMessage: BuyBonusRejectedMessage = {
                            type: GameUIMessageType.BUY_BONUS_REJECTED,
                        }

                        window.postMessage(buyBonusRejectedMessage)
                    }

                    setFSBonuses(freeSpins)

                    if (freeSpins.length === 1) {
                        setFSBonusSystemState(BonusSystemState.SINGLE_BONUS_INITIAL)
                    } else if (freeSpins.length > 1) {
                        setFSBonusSystemState(BonusSystemState.MULTIPLE_BONUSES_INITIAL)
                    }

                    // FREE BUY BONUS
                    const bonuses = data.bonuses || currentGameBonuses
                    const freeBuyBonuses = modifyFreeBuyBonuses(bonuses, bonusFeaturesRef.current)

                    if (bonuses.length > 1) {
                        setBundleImageName(BonusImages.FREE_BUY_BONUS_BUNDLE)
                    }

                    setHasAwardedFreeBuyBonuses(freeBuyBonuses.length >= 1)
                    setFBBonuses(freeBuyBonuses)

                    if (freeBuyBonuses.length === 1) {
                        setFBBonusSystemState(BonusSystemState.SINGLE_BONUS_INITIAL)
                    } else if (freeBuyBonuses.length > 1) {
                        setFBBonusSystemState(BonusSystemState.MULTIPLE_BONUSES_INITIAL)
                    }

                    break
                }

                case GameUIMessageType.ACTIVE_BONUS_FINISHED: {
                    const data = rawData as ActiveBonusFinishedMessage
                    const { totalWin, hasMoreBonuses, isNextBonusFBB, shouldShowEndModal, bonusType } = data

                    if (bonusType === BonusTypes.FREE_BUY_BONUS && buyBonusConfigFromStore.shouldTheFeatureStayActive) {
                        // set last active bonus so we can access it in the FBBEndButton component
                        // we dont have enough info on recovery though so this is why we map the bonus with it's features again here
                        const freeBuyBonuses = modifyFreeBuyBonuses([activeFBBonus!], bonusFeaturesRef.current)

                        setFBBLastBonus(freeBuyBonuses[0])
                        setHasFBBActivatedBonus(false)
                    }

                    setFBBActiveBonus(undefined)
                    setFBBTotalWin(`${totalWin}`)

                    if (!isNextBonusFBB) {
                        setHasAwardedFreeBuyBonuses(false)
                        setFBHasActiveBonusesLeft(false)
                    }

                    if (bonusType === BonusTypes.COUNTDOWN_FREE_SPIN) {
                        setCFSTotalWin(`${totalWin}`)
                        setCFSHasActiveBonusEnded(true)
                        setCFSHasActiveBonusesLeft(hasMoreBonuses)
                        setCFSShowRemainingSpinsBanner(false)
                        setIsCFSModalOpened(true)
                    }

                    if (hasMoreBonuses && bonusFeaturesRef.current.length > 0 && isNextBonusFBB) {
                        const activeBonusIndex = FBBActiveBonus
                            ? FBBBonuses.findIndex((b) => b.id === FBBActiveBonus.id)
                            : 0

                        const findNextBonus = (nextIndex = activeBonusIndex + 1): Bonus | undefined => {
                            const next = FBBBonuses[nextIndex]

                            // In theory next should exist since isNextBonusFBB is true - but for cases where fbb is awarded during
                            // the lucky spins winning bet next will not exist and this will help the game recover after
                            // bonuses are played out rather than crash
                            // This is an edge case which will be handled after live
                            if (next) {
                                if (!next.isUnused) {
                                    const newNextIndex = nextIndex + 1

                                    return newNextIndex >= FBBBonuses.length ? undefined : findNextBonus(newNextIndex)
                                }

                                return next
                            }
                        }

                        setFBHasActiveBonusesLeft(true)

                        setFBBActiveBonus(findNextBonus() ?? FBBBonuses[0])
                    }

                    if (bonusType === BonusTypes.FREE_SPIN) {
                        setFSTotalWin(`${totalWin}`)
                        setFSHasActiveBonusEnded(true)
                        setFSHasActiveBonusesLeft(hasMoreBonuses)
                        setFSShowRemainingSpinsBanner(false)
                    }

                    if (shouldShowEndModal) {
                        if (bonusType === BonusTypes.COUNTDOWN_FREE_SPIN) {
                            setCFSBonusSystemState(BonusSystemState.END)
                        }

                        if (bonusType === BonusTypes.FREE_SPIN) {
                            setFSBonusSystemState(BonusSystemState.END)
                        }

                        if (bonusType === BonusTypes.FREE_BUY_BONUS) {
                            setFBBonusSystemState(BonusSystemState.END)
                        }
                    }

                    if (bonusType !== BonusTypes.LUCKY_SPIN) return

                    setLSActiveBonus(undefined)
                    setLSIsIntro(false)
                    setLSTotalWin(`${totalWin}`)
                    setIsLSModalOpened(true)
                    setLSShowRemainingSpinsBanner(false)

                    gsap.delayedCall(3, () => {
                        setIsLSModalOpened(false)

                        const bonusEndModalButtonClickedMessage: FreespinBonusEndModalButtonClickedMessage = {
                            type: GameUIMessageType.FREESPIN_BONUS_END_MODAL_BUTTON_CLICKED,
                        }

                        window.postMessage(bonusEndModalButtonClickedMessage)
                    })

                    break
                }

                case GameUIMessageType.REMAINING_FREE_SPINS_UPDATE: {
                    const data = rawData as RemainingFreeSpinsUpdateMessage
                    const {
                        totalWin,
                        remainingSpins,
                        betValue,
                        bonusType,
                        isUnused,
                        triggeringBetCompleted,
                        aggregatedGameStarted,
                        isTriggeringBet,
                    } = data

                    if (bonusType !== BonusTypes.FREE_SPIN && bonusType !== BonusTypes.LUCKY_SPIN) return

                    const shouldShowBanner =
                        bonusType === BonusTypes.FREE_SPIN ||
                        (bonusType === BonusTypes.LUCKY_SPIN && (triggeringBetCompleted || !isUnused))

                    // if this is the triggering bet, we want to set the autoplay button to act as collect
                    if (
                        bonusType === BonusTypes.LUCKY_SPIN &&
                        triggeringBetCompleted &&
                        isUnused &&
                        aggregatedGameStarted &&
                        isTriggeringBet
                    ) {
                        setAreLSAboutToStart(true)

                        return
                    }

                    if (bonusType === BonusTypes.LUCKY_SPIN && (triggeringBetCompleted || !isUnused)) {
                        setLSTotalWin(`${totalWin}`)
                        setAreLSAboutToStart(false)
                        setLSRemainingSpins(remainingSpins)
                        setLSActiveBet(betValue)
                        setLSShowRemainingSpinsBanner(shouldShowBanner)
                        setLSIsIntro(true)

                        return
                    }

                    setFSTotalWin(`${totalWin}`)
                    setFSRemainingSpins(+remainingSpins)
                    setFSActiveBet(betValue)
                    setFSShowRemainingSpinsBanner(shouldShowBanner)

                    break
                }

                case GameUIMessageType.COUNTDOWN_FREE_SPINS_UPDATE: {
                    const data = rawData as CountdownFreeSpinsUpdateMessage
                    const { totalWin, bonus } = data

                    if (bonus.type !== BonusTypes.COUNTDOWN_FREE_SPIN) return

                    setCFSTotalWin(`${totalWin}`)
                    setCFSActiveBet(bonus.betValue)
                    setCFSActiveBonus(bonus)
                    setCFSShowRemainingSpinsBanner(true)

                    break
                }

                case GameUIMessageType.BONUSES_CLAIMING_ERROR: {
                    const data = rawData as BonusesClaimingErrorMessage
                    const { isBonusAlreadyClaimed } = data.payload ?? {}

                    setBonusSpinsClaimingError(true)
                    setFBBClaimingError(true)
                    setCFSClaimingError(true)

                    setHasFBBActivatedBonus(false)
                    setFBBActiveBonus(undefined)

                    setFSBonusSystemState('')
                    setFBBonusSystemState('')
                    setCFSBonusSystemState('')

                    setSelectedFeatureIndex(0)
                    setSelectedFeature(null)
                    updateCurrentFeatureMultiplier(1)
                    setMultiChoiceToggle(false)

                    setIsFBBonusAlreadyClaimed(!!isBonusAlreadyClaimed)
                    setIsFSBonusAlreadyClaimed(!!isBonusAlreadyClaimed)
                    setIsCFSBonusAlreadyClaimed(!!isBonusAlreadyClaimed)

                    break
                }

                case GameUIMessageType.TOGGLE_LEFT_HAND_MODE: {
                    const { payload } = rawData as ToggleLeftHandModeMessage

                    updateIsLeftHandMode(payload)
                    break
                }
                case GameUIMessageType.HAS_PENDING_JACKPOT: {
                    setJackpotPhase(JackpotPhase.PENDING)
                    break
                }
                case GameUIMessageType.JACKPOT_RECOVERY: {
                    const { jackpotGameState } = rawData as JackpotRecoveryMessage

                    setJackpotGameState(jackpotGameState)
                    setJackpotPhase(JackpotPhase.RUNNING)
                    setJackpotMode(JackpotMode.STARTED)
                    break
                }
                case GameUIMessageType.JACKPOT_EQ_RECOVERY: {
                    const { levelIndex } = rawData as JackpotEQRecoveryMessage

                    setFreezeIndex(levelIndex)
                    setIsJackpotEQStarted(true)
                    break
                }
            }
        }

        window.addEventListener('message', handleMessage, false)

        return () => {
            window.removeEventListener('message', handleMessage, false)
        }
    }, [FBBActiveBonus, FBBBonuses, hasBuyBonus, buyBonusConfigFromStore, isDoubleChanceActivated])

    const renderGameComponent = () => {
        if (isInStorybook) {
            return <Preview />
        }

        return <Game />
    }

    useEffect(() => {
        // TODO: Can be deleted after all games are on prod with PIXI 8. This fixes issues in platform with desync
        // language and wrong paytable url for old menu (pixi menu)
        if (!shouldUseExternalMenu) {
            patchPaytableUrl(language)
        }
    }, [language, shouldUseExternalMenu])

    return (
        <>
            {isStandalone && <PostMessageSystemUI isLoaded={isGameLoaded} />}

            <ScaledWrapper className={cn(viewport, 'overflow-hidden')} onUpdateScale={setStageScale}>
                {!isGameLoaded && <div className="w-full h-full absolute inset-0 z-[100000] bg-black" />}
                {/* TODO: remove when promo removed from platform */}
                {!isHistoryMode && !isCFSRecovery && !isLSRecovery && !isFSRecovery && !hasWidgetInUI && (
                    <NotificationPopup shouldShow={showLuckySpins} duration={3000} type="lucky" />
                )}
                {renderGameComponent()}
                <div ref={mainComponents}>{renderMainComponents}</div>
                {!isHistoryMode && <FeatureAutostartCounterController key="press-start-counter-controller" />}
                {hasCustomResult && <CustomResult />}
                {shouldShowPaytableConfirmation && !isPaytableConfirmationAccepted && <PaytableConfirmation />}
                {hasUltraPlay && <UltraPlayModal />}
            </ScaledWrapper>
        </>
    )
}

export default App
